import React, { useState } from 'react';
import { CustomMeasure } from '../../../services/firebase/FirebaseCustomMeasureService';

interface CustomMeasureFormProps {
  measure: CustomMeasure;
  onComplete: (answers: Record<string, any>) => void;
}

const CustomMeasureForm: React.FC<CustomMeasureFormProps> = ({ measure, onComplete }) => {
  const [showReview, setShowReview] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    measure.questions.forEach(q => { initial[q.id] = ''; });
    return initial;
  });

  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const isAllAnswered = () => measure.questions.every(q => answers[q.id] !== '');

  const calculateScore = () => {
    return measure.questions.reduce((total, q) => {
      const val = parseInt(answers[q.id] || '0', 10);
      return total + (isNaN(val) ? 0 : val);
    }, 0);
  };

  const getSeverity = (score: number) => {
    for (const band of measure.bands) {
      if (score >= band.min && score <= band.max) return band;
    }
    return measure.bands[measure.bands.length - 1];
  };

  const getAnswerLabel = (value: string) => {
    return measure.scaleLabels.find(sl => sl.value === value)?.label || value;
  };

  const answeredCount = Object.values(answers).filter(a => a !== '').length;

  if (showReview) {
    const score = calculateScore();
    const severity = getSeverity(score);
    return (
      <div className="app">
        <div style={{
          maxWidth: '900px', margin: '0 auto', background: '#28527A',
          borderRadius: '16px', padding: '2rem',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', marginTop: '2rem'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <h1 style={{ color: '#f9fafb', fontSize: '1.75rem', margin: '0 0 0.5rem 0' }}>
              {measure.title} — Review
            </h1>
            <p style={{ color: '#6CA6C1', margin: 0 }}>Please review your answers before submitting</p>
          </div>

          <div style={{
            background: '#113F67', borderRadius: '12px', border: '1px solid #6CA6C1',
            padding: '2rem', marginBottom: '2rem'
          }}>
            <h3 style={{ color: '#f9fafb', marginBottom: '1.5rem' }}>Your Responses</h3>

            {measure.description && (
              <p style={{ color: '#d1d5db', marginBottom: '1rem', fontSize: '0.9rem' }}>{measure.description}</p>
            )}

            {measure.questions.map((q, i) => (
              <div key={q.id} style={{
                background: '#1f2937', padding: '1rem', borderRadius: '8px',
                marginBottom: '0.75rem', border: '1px solid #374151'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#f9fafb', fontSize: '0.9rem', flex: 1 }}>
                    {i + 1}. {q.text}
                  </span>
                  <span style={{
                    color: '#10b981', fontSize: '0.875rem', fontWeight: 'bold',
                    minWidth: '150px', textAlign: 'right'
                  }}>
                    {getAnswerLabel(answers[q.id])} ({answers[q.id]})
                  </span>
                </div>
              </div>
            ))}

            {/* Score Summary */}
            <div style={{
              background: severity.color + '22', borderRadius: '8px', padding: '1.5rem',
              border: `1px solid ${severity.color}`, textAlign: 'center', marginTop: '1.5rem'
            }}>
              <h4 style={{ color: '#f9fafb', margin: '0 0 0.5rem 0' }}>
                Score: {score}/{measure.maxScore}
              </h4>
              <p style={{ color: severity.color, margin: 0, fontSize: '0.875rem', fontWeight: 'bold' }}>
                {severity.label}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button onClick={() => setShowReview(false)} style={{
              padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none',
              background: '#374151', color: '#f9fafb', fontSize: '1rem', cursor: 'pointer'
            }}>
              Back to Edit
            </button>
            <button onClick={() => onComplete(answers)} style={{
              background: 'linear-gradient(135deg, #D4A017 0%, #B8860B 100%)',
              color: 'white', padding: '0.75rem 3rem', fontSize: '1rem', fontWeight: '600',
              border: 'none', borderRadius: '8px', cursor: 'pointer',
              textTransform: 'uppercase', letterSpacing: '0.05em',
              boxShadow: '0 4px 14px 0 rgba(212, 160, 23, 0.3)'
            }}>
              Submit & Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div style={{
        maxWidth: '900px', margin: '0 auto', background: '#28527A',
        borderRadius: '16px', padding: '2rem',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', marginTop: '2rem'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ color: '#f9fafb', fontSize: '1.75rem', margin: '0 0 0.5rem 0' }}>
            {measure.title}
          </h1>
          {measure.description && (
            <p style={{ color: '#6CA6C1', margin: '0 0 1rem 0', fontSize: '1rem' }}>
              {measure.description}
            </p>
          )}
          <p style={{ color: '#6CA6C1', margin: 0 }}>Please complete all questions below</p>
        </div>

        {/* Questions */}
        <div style={{
          background: '#113F67', borderRadius: '12px', border: '1px solid #6CA6C1',
          padding: '2rem', marginBottom: '2rem'
        }}>
          <div style={{ marginBottom: '2rem' }}>
            <p style={{ color: '#f9fafb', fontSize: '1rem', marginBottom: '0.5rem' }}>
              <span style={{ color: '#ef4444' }}>*</span> Indicates a required field
            </p>
          </div>

          {measure.questions.map((q, i) => (
            <div key={q.id} style={{ marginBottom: '2rem' }}>
              <h3 style={{
                color: '#f9fafb', fontSize: '1rem', marginBottom: '1rem',
                display: 'flex', alignItems: 'flex-start'
              }}>
                <span style={{ color: '#ef4444', marginRight: '0.25rem' }}>*</span>
                {i + 1}. {q.text}
              </h3>

              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {measure.scaleLabels.map((option) => (
                  <label key={option.value} style={{
                    display: 'flex', alignItems: 'center', color: '#d1d5db',
                    cursor: 'pointer', padding: '0.75rem',
                    background: answers[q.id] === option.value ? '#1f2937' : '#374151',
                    borderRadius: '8px',
                    border: answers[q.id] === option.value ? '2px solid #3b82f6' : '1px solid #4b5563',
                    transition: 'all 0.2s ease'
                  }}>
                    <input
                      type="radio"
                      name={q.id}
                      value={option.value}
                      checked={answers[q.id] === option.value}
                      onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                      style={{ marginRight: '0.75rem', width: '16px', height: '16px' }}
                    />
                    {option.value} — {option.label}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Submit Button */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={() => { if (isAllAnswered()) setShowReview(true); }}
            disabled={!isAllAnswered()}
            style={{
              background: isAllAnswered()
                ? 'linear-gradient(135deg, #D4A017 0%, #B8860B 100%)'
                : '#6b7280',
              color: 'white', padding: '0.75rem 3rem', fontSize: '1rem',
              fontWeight: '600', border: 'none', borderRadius: '8px',
              cursor: isAllAnswered() ? 'pointer' : 'not-allowed',
              textTransform: 'uppercase', letterSpacing: '0.05em',
              boxShadow: isAllAnswered() ? '0 4px 14px 0 rgba(212, 160, 23, 0.3)' : 'none',
              opacity: isAllAnswered() ? 1 : 0.6
            }}
          >
            Review & Continue
          </button>
        </div>

        {/* Progress */}
        <div style={{
          textAlign: 'center', marginTop: '1.5rem', padding: '1rem',
          background: '#065f46', borderRadius: '8px', border: '1px solid #10b981'
        }}>
          <div style={{ color: '#d1d5db', fontSize: '0.875rem' }}>
            Progress: {answeredCount} of {measure.questions.length} questions completed
          </div>
        </div>

        {measure.citation && (
          <p style={{ color: '#6b7280', fontSize: '0.75rem', textAlign: 'center', marginTop: '1rem' }}>
            {measure.citation}
          </p>
        )}
      </div>
    </div>
  );
};

export default CustomMeasureForm;
